import { Button, Icon, Input, Label, RadioGroup } from '@components';
import { acceptedInputTypes } from '@components/file-upload/constants';
import {
  certificateOptions,
  CertificateType
} from '@features/authentication/constants/common';
import { bytesToMB } from '@utils/file';

import theme from '../../../../themes/colors';

interface PanDocumentProps {
  pan: string;
  onPanChange: (value: string) => void;
  panError: string;
  panFile: File | null;
  panFileRef: React.RefObject<HTMLInputElement>;
  handlePanFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleGstinFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removePanFile: () => void;
  removeGstinFile: () => void;
  existingDocs: { type: string; url: string }[];
  onDownload: (type: string) => void;
  removeExistingPanDoc: () => void;
  selectedBusinessType: 'organisation' | 'individual';
  certificateType: string;
  setCertificateType: (value: string) => void;
  onGstinChange: (value: string) => void;
  gstin: string;
  gstinError: string;
  gstinFile: File | null;
  gstinFileRef: React.RefObject<HTMLInputElement>;
}

const PanDocument = ({
  pan,
  onPanChange,
  panError,
  panFile,
  panFileRef,
  handlePanFileSelect,
  handleGstinFileSelect,
  removePanFile,
  removeGstinFile,
  existingDocs,
  onDownload,
  removeExistingPanDoc,
  selectedBusinessType,
  certificateType,
  setCertificateType,
  onGstinChange,
  gstin,
  gstinError,
  gstinFile,
  gstinFileRef
}: PanDocumentProps) => {
  const existingPanDoc = existingDocs?.find((doc) => doc.type === 'PAN');
  const existingGstinDoc = existingDocs?.find(
    (doc) => doc.type === 'GST_CERTIFICATE'
  );

  const hasPanFile = !!(panFile || existingPanDoc);
  const hasGstinFile = !!(gstinFile || existingGstinDoc);

  const panFileName =
    panFile?.name ||
    decodeURIComponent(
      existingPanDoc?.url.split('/').pop()?.split('?')[0] || ''
    ) ||
    '';

  const gstinFileName =
    gstinFile?.name ||
    decodeURIComponent(
      existingGstinDoc?.url.split('/').pop()?.split('?')[0] || ''
    ) ||
    '';

  const isPan = certificateType === CertificateType.PAN;

  const removeFile = () => {
    if (isPan ? panFile : gstinFile) {
      if (isPan) removePanFile();
      else removeGstinFile();
    } else {
      removeExistingPanDoc();
    }
  };

  return (
    <div>
      {selectedBusinessType === 'organisation' ? (
        <div className="mb-1">
          <RadioGroup
            options={certificateOptions}
            handleChange={setCertificateType}
            selectedValue={certificateType}
            labelStyle="!text-cofeeNeutral10 !font-semibold !text-sm !ml-2"
          />
        </div>
      ) : (
        <Label label={isPan ? 'PAN' : 'GSTIN'} isRequired className="!mb-1" />
      )}

      <div className="flex">
        <Input
          name={isPan ? 'pan' : 'gstin'}
          value={isPan ? pan : gstin}
          placeholder={isPan ? 'AAAPA1234A' : '22AAAAAA0000A1Z5'}
          handleInputChange={isPan ? onPanChange : onGstinChange}
          className="flex-1"
          inputStyle="!rounded-r-none"
          maxLength={isPan ? 10 : 15}
          error={!!(isPan ? panError : gstinError)}
          errorMessage={isPan ? panError : gstinError}
        />
        <input
          type="file"
          ref={isPan ? panFileRef : gstinFileRef}
          className="hidden"
          onChange={isPan ? handlePanFileSelect : handleGstinFileSelect}
          accept={acceptedInputTypes}
          disabled={isPan ? hasPanFile : hasGstinFile}
        />
        <div
          role="presentation"
          onClick={() =>
            isPan
              ? !hasPanFile && panFileRef.current?.click()
              : !hasGstinFile && gstinFileRef.current?.click()
          }
          className={`flex h-12.5 shrink-0 items-center gap-2 rounded-r-xl border border-l-0 border-cofeeGreyBlue5 bg-cofeeNeutral1 px-4 text-sm font-medium ${
            (isPan ? hasPanFile : hasGstinFile)
              ? 'cursor-not-allowed text-cofeeNeutral7 opacity-60'
              : 'cursor-pointer text-primary opacity-100'
          }`}
        >
          {(isPan ? hasPanFile : hasGstinFile) ? (
            <Icon name="upload-file-neutral" size="medium" />
          ) : (
            <Icon name="upload-file" size="medium" />
          )}
          Upload
        </div>
      </div>
      {(isPan ? hasPanFile : hasGstinFile) ? (
        <div
          className={`flex items-center gap-1 text-sm text-grey ${
            (isPan ? panError : gstinError) ? 'mt-5' : 'mt-1'
          }`}
        >
          <Button
            variant="transparent"
            handleClick={
              (isPan ? existingPanDoc : existingGstinDoc)
                ? () => onDownload(isPan ? 'PAN' : 'GST_CERTIFICATE')
                : undefined
            }
            className={` max-w-400 !justify-start truncate !pl-0 text-green-600 underline ${
              (isPan ? existingPanDoc : existingGstinDoc)
                ? 'cursor-pointer text-green-600 underline'
                : ''
            }`}
          >
            <span className="max-w-96 !justify-start truncate">
              {isPan ? panFileName : gstinFileName}
            </span>
          </Button>
          {(isPan ? panFile : gstinFile) && <span>|</span>}
          {(isPan ? panFile : gstinFile) && (
            <span>{bytesToMB(isPan ? panFile?.size : gstinFile?.size)}</span>
          )}
          <span>|</span>
          <span
            role="presentation"
            onClick={removeFile}
            className="cursor-pointer text-error"
          >
            Delete
          </span>
        </div>
      ) : (
        <div
          className={`flex items-center gap-1 text-xs font-normal text-cofeeNeutral7 ${
            (isPan ? panError : gstinError) ? 'mt-5' : 'mt-1.5'
          }`}
        >
          <Icon name="info-circle" stroke={theme.cofeeNeutral8} size="none" />
          Supported formats are PDF, JPG, JPEG, or PNG format
        </div>
      )}
    </div>
  );
};

export default PanDocument;
